import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, Optional, Renderer2 } from '@angular/core';
import { ICategoryDataV3 } from '@df/catalog/category/category';
import { App } from '@df/core/app';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { FacetUrlService } from '@df/module-facet-url/core/facet-url.service';
import { NFacetUrl } from '@df/module-facet-url/core/facet.interfaces';
import { BgColorOptions, UndefinedOption, atomicTextStyle } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { HTMLBindingDirective } from '@df/ui/common/html-binding.directive';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Subscription } from 'rxjs';
import { CategoryViewComponent } from '../../../category/category-view/category-view.component';
import { Category } from '../../../category/category.decorator';
let uid = 0;
interface IFaqItem {
    answer?: string;
    answer_size?: string;
    question?: string;
    question_size?: string;
}
interface IPageComponentFaqData extends NICmsContentComponent.IData {
    background_color?: string;
    header?: string;
    elements?: IFaqItem[];
    extra_copy?: string;
    cta_link?: string;
    cta_copy?: string;
    hideOnFacetPlps?: boolean;
    hideOnNoFacetPlps?: boolean;
}
@ICmsComponent<PageComponentFaqComponent>({
    id: 'faq',
    name: 'FAQs',
    tags: ['FAQs', 'accordion'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'background_color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            title: 'Background Color',
            options: BgColorOptions,
            description: 'Background color'
        },
        <NICmsContentField.IField>{
            key: 'header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'elements',
            title: 'Elements',
            type: NICmsContentField.EFieldType.repeater,
            itemName: 'Item',
            description: 'content of this elements can be overwritten by facet content',
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'question',
                    title: 'Question'
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'question_size',
                    title: 'Question Font Size',
                    options: atomicTextStyle
                },
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'answer',
                    title: 'Answer'
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'answer_size',
                    title: 'Answer Font Size',
                    options: [...UndefinedOption, ...atomicTextStyle]
                }
            ]
        },
        <NICmsContentField.IField>{
            key: 'extra_copy',
            type: NICmsContentField.EFieldType.text,
            title: 'Extra copy'
        },
        <NICmsContentField.IField>{
            key: 'cta_copy',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA copy'
        },
        <NICmsContentField.IField>{
            key: 'cta_link',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA link'
        },
        <NICmsContentField.IField>{
            key: 'hideOnFacetPlps',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide on facet PLPs',
            description: 'custom facet rules FAQs will still be shown',
            default: false
        },
        <NICmsContentField.IField>{
            key: 'hideOnNoFacetPlps',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide on main (not facet) PLPs',
            default: false
        }
    ]
})
@Component({
    selector: 'page-component-faq',
    templateUrl: 'faq.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, IfSizeDirective, HrefComponent, ZippyModule, ICmsClientModule, HTMLBindingDirective]
})
export class PageComponentFaqComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'component'
    })
    component?: IPageComponentFaqData;

    uid = `page-component-faq-${uid++}`;

    listingSubscription?: Subscription;

    faqs?: IFaqItem[];

    header?: string;
    constructor(
        private app: App,
        public elementRef: ElementRef,
        protected renderer2: Renderer2,
        protected facetUrlService: FacetUrlService,
        @Optional() protected categoryView: CategoryViewComponent
    ) {
        super();
    }

    get category(): Category<ICategoryDataV3> | undefined {
        return this.categoryView?.model;
    }

    get facetData(): NFacetUrl.IFacetData | undefined {
        return this.facetUrlService.retrieveFacetData(this.category?.id as number);
    }

    get canShow(): boolean {
        const isPlp = !!this.categoryView;
        const isFacetPlp = !!this.app.meta.state?.route?.data?.facet?.category;
        const hasFacetFaqData = !!this.facetData?.['faq'];
        return !(
            (isPlp &&
                ((isFacetPlp && this.component?.hideOnFacetPlps) || (!isFacetPlp && this.component?.hideOnNoFacetPlps)) &&
                !hasFacetFaqData) ||
            !this.component
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.app.meta.subscribe(() => this.detectChanges()));
        this.subscribeToListing();
        this.subscriptions.add(
            this.categoryView?.categoryViewModelChangeSubject.subscribe(() => {
                this.subscribeToListing();
                this.detectChanges();
            })
        );
        this.onListingChange();
        this.subscriptions.add(this.app.meta.subscribe(() => this.detectChanges()));
    }

    subscribeToListing(): void {
        this.listingSubscription?.unsubscribe();
        this.listingSubscription = this.categoryView?.listing?.subscribe(() => this.detectChanges());
    }

    override detectChanges(): void {
        this.onListingChange();
        super.detectChanges();
    }

    onListingChange(): void {
        const facetData = this.facetData;
        this.faqs =
            facetData?.['faq']?.map(faq => {
                return {
                    answer: faq.answer,
                    question: faq.question
                };
            }) || this.component?.elements;
        this.header = facetData?.faq_title || this.component?.header
        this.canShow || this.client.isCms
            ? this.renderer2.removeClass(this.elementRef.nativeElement, 'ng-hide')
            : this.renderer2.addClass(this.elementRef.nativeElement, 'ng-hide');
    }

}
